// ✅ src/pages/VehicleV2/VehicleBreakdownDetailedReport_Print.jsx
// ✅ React print page (NO Crystal) for "Vehicle Breakdown Detailed Report"
// ✅ Same print DESIGN as your TyreMaster print template (Nissi header + bordered blocks)
// ✅ Reads filters from URL query string and calls backend print API:
//    GET http://nissi-india.com/api/vehicleBreakdownReport/detailed/print?... (recommended)
// If your backend uses a different route, just change API_BASE + endpoint.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/vehicleBreakdownDetailReport";

/* helpers */
const toStr = (v) => String(v ?? "").trim();
const pad2 = (n) => String(n).padStart(2, "0");

const fmtDate = (iso) => {
  if (!iso) return "";
  if (typeof iso === "string" && iso.includes("/")) return iso; // already dd/MM/yyyy
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
};

const toNumStr = (v, digits = 2) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  if (!Number.isFinite(n)) return toStr(v);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

// ✅ normalize detailed row (keep flexible, your SP may return different names)
function normalizeDetailedRow(r) {
  return {
    branchName: pick(r, ["fld_branchname", "BranchName", "branchName", "branch"]),
    vehicleMainType: pick(r, ["VehicleType", "vehicleMainType", "VehicleMainType", "mainType"]),
    vehicleName: pick(r, ["fld_vehicle_name", "VehicleName", "vehicleName", "name"]),
    vehicleCode: pick(r, ["fld_vehicle_code", "VehicleCode", "vehicleCode"]),
    vehicleRegNo: pick(r, ["fld_vehicle_regno", "VehicleRegNo", "vehicleRegNo", "regNo"]),

    status: pick(r, ["fld_status", "Status", "status"]),
    progressDate: fmtDate(pick(r, ["fld_progressentrydate", "progressDate", "date"])),
    remarks: pick(r, ["fld_remarks", "remarks", "Remarks"]),
    fromDate: fmtDate(pick(r, ["fld_fromdate", "FromDate", "fromDate"])),
    toDate: fmtDate(pick(r, ["fld_todate", "ToDate", "toDate"])),

    runningHr: toNumStr(pick(r, ["Whrs", "runningHr", "RunningHr"], "0"), 2),
    cumHr: toNumStr(pick(r, ["cumulativehrs", "cumHr", "CumHr"], "0"), 2),
    runningKm: toNumStr(pick(r, ["WKM", "runningKm", "RunningKm"], "0"), 2),
    cumKm: toNumStr(pick(r, ["cumulativekm", "cumKm", "CumKm"], "0"), 2),
  };
}

export default function VehicleBreakdownDetailedReport_Print() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  // ✅ Read same filters you used in list page
  const filters = useMemo(() => {
    const get = (k, d = "") => toStr(searchParams.get(k) ?? d);

    return {
      vehicleBasis: get("vehicleBasis", "OWN"), // OWN | HIRE
      month: get("month", "Select"),
      year: get("year", "Select"),
      branchId: get("branchId", "Select"),
      vehicleCode: get("vehicleCode", ""),
      vehicleName: get("vehicleName", ""),
      regNo: get("regNo", ""),
      vehicleMainType: get("vehicleMainType", "Select"),
      status: get("status", "Breakdown"),
      searchFromDate: get("searchFromDate", ""),
      searchToDate: get("searchToDate", ""),
    };
  }, [searchParams]);

  // ✅ call detailed print API
  const printUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("vehicleBasis", filters.vehicleBasis || "OWN");
    params.set("month", filters.month || "Select");
    params.set("year", filters.year || "Select");
    params.set("branchId", filters.branchId || "Select");
    params.set("vehicleCode", filters.vehicleCode || "");
    params.set("vehicleName", filters.vehicleName || "");
    params.set("regNo", filters.regNo || "");
    params.set("vehicleMainType", filters.vehicleMainType || "Select");
    params.set("status", filters.status || "Breakdown");
    params.set("searchFromDate", filters.searchFromDate || "");
    params.set("searchToDate", filters.searchToDate || "");

    // 🔁 change endpoint if needed
    return `${API_BASE}/print?${params.toString()}`;
  }, [filters]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await axios.get(printUrl);
        if (!res?.data?.success) throw new Error(res?.data?.message || "Failed to load print data");

        if (!alive) return;
        setRows(res?.data?.rows || []);
      } catch (e) {
        if (!alive) return;
        setRows([]);
        setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [printUrl]);

  const normalizedRows = useMemo(() => rows.map(normalizeDetailedRow), [rows]);

  const headerMonth = filters.month && filters.month !== "Select" ? filters.month : "Select";
  const headerYear = filters.year && filters.year !== "Select" ? filters.year : "Select";

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      <style>{`
        @media print {
          body { margin: 0; padding: 10px; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
        }
        @page { size: A4 landscape; margin: 0.3in; }
      `}</style>

      <div className="p-2">
        {/* Top actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
          >
            🖨️ Print
          </button>
        </div>

        {/* Loading/Error */}
        {loading ? (
          <div className="no-print p-3 border rounded bg-gray-50 text-sm mb-2">
            Loading print data...
          </div>
        ) : err ? (
          <div className="no-print p-3 border rounded bg-red-50 text-sm text-red-700 mb-2">
            {err}
          </div>
        ) : null}

        {/* Header (same style as your Tyre print) */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
              </div>
              <div className="text-xs">
                Phone No : 9003744644 &nbsp;&nbsp;&nbsp;&nbsp;
                infracnsoft-implementation@nissiinfotech.in
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <h2 className="text-base font-bold">Vehicle BreakDown Detailed Report</h2>
        </div>

        {/* Month / Year line */}
        <div className="mb-2 border-2 border-black p-1 text-xs">
          <span className="font-bold mr-2">Month</span>: {headerMonth}
          <span className="font-bold mx-6">Year</span>: {headerYear}
          <span className="font-bold mx-6">Status</span>: {filters.status || "Breakdown"}
        </div>

        {/* Table (detailed) */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 font-bold text-center">S.No</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Branch Name</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Main Type</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Name</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Code</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle RegNo</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Status</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Progress Date</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Remarks</th>
                <th className="border border-black px-1 py-1 font-bold text-center">From Date</th>
                <th className="border border-black px-1 py-1 font-bold text-center">To Date</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Running Hr</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Cum.Hr</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Running KM</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Cum.KM</th>
              </tr>
            </thead>

            <tbody>
              {normalizedRows.length === 0 ? (
                <tr>
                  <td colSpan={15} className="border border-black px-2 py-2 text-center">
                    No Records Found
                  </td>
                </tr>
              ) : (
                normalizedRows.map((r, idx) => (
                  <tr key={`${r.vehicleCode}-${idx}`} className="bg-white">
                    <td className="border border-black px-1 py-1 text-center">{idx + 1}</td>
                    <td className="border border-black px-1 py-1">{r.branchName}</td>
                    <td className="border border-black px-1 py-1">{r.vehicleMainType}</td>
                    <td className="border border-black px-1 py-1">{r.vehicleName}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.vehicleCode}</td>
                    <td className="border border-black px-1 py-1">{r.vehicleRegNo}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.status}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.progressDate}</td>
                    <td className="border border-black px-1 py-1">{r.remarks}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.fromDate}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.toDate}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.runningHr}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.cumHr}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.runningKm}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.cumKm}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <div className="flex items-center justify-between">
            <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
            <p className="font-bold">INFRA SCREEN : B-57/3</p>
          </div>
        </div>
      </div>
    </div>
  );
}
